import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  RefreshCw,
  Filter,
  Download,
  AlertCircle,
  AlertTriangle,
  Info,
  Bug,
  Pause,
  Play,
  Trash2,
  ExternalLink,
} from 'lucide-react';
import { format } from 'date-fns';
import { clsx } from 'clsx';
import { io, Socket } from 'socket.io-client';

const API_BASE = '/api';

// Types
interface LogEntry {
  id: string;
  level: 'error' | 'warn' | 'info' | 'debug';
  message: string;
  context?: Record<string, unknown>;
  orderId?: string;
  source?: string;
  createdAt: string;
}

interface LogsResponse {
  data: {
    data: LogEntry[];
    pagination: {
      page: number;
      pageSize: number;
      totalItems: number;
      totalPages: number;
    };
  };
}

const levelConfig: Record<string, { icon: React.ElementType; color: string; bgColor: string }> = {
  error: { icon: AlertCircle, color: 'text-red-600', bgColor: 'bg-red-50' },
  warn: { icon: AlertTriangle, color: 'text-yellow-600', bgColor: 'bg-yellow-50' },
  info: { icon: Info, color: 'text-blue-600', bgColor: 'bg-blue-50' },
  debug: { icon: Bug, color: 'text-gray-600', bgColor: 'bg-gray-50' },
};

export default function LogsPage() {
  const [filter, setFilter] = useState<string>('');
  const [levelFilter, setLevelFilter] = useState<string>('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamLogs, setStreamLogs] = useState<LogEntry[]>([]);
  const socketRef = useRef<Socket | null>(null);
  const logsEndRef = useRef<HTMLDivElement>(null);

  // Fetch historical logs
  const { data, isLoading, refetch } = useQuery<LogsResponse>({
    queryKey: ['logs', levelFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('pageSize', '100');
      if (levelFilter) params.set('level', levelFilter);
      const res = await fetch(`${API_BASE}/dev/logs?${params}`);
      return res.json();
    },
    refetchInterval: isStreaming ? false : 10000,
  });

  // WebSocket connection for real-time logs
  useEffect(() => {
    if (isStreaming) {
      const socket = io('/', { path: '/socket.io' });
      socketRef.current = socket;

      socket.on('connect', () => {
        socket.emit('subscribe', ['logs']);
      });

      socket.on('log:entry', (log: LogEntry) => {
        setStreamLogs((prev) => [log, ...prev].slice(0, 200));
      });

      return () => {
        socket.emit('unsubscribe', ['logs']);
        socket.disconnect();
      };
    }
  }, [isStreaming]);

  // Auto-scroll to new logs
  useEffect(() => {
    if (isStreaming && streamLogs.length > 0) {
      logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [streamLogs, isStreaming]);

  const toggleStreaming = () => {
    if (isStreaming) {
      socketRef.current?.disconnect();
      setStreamLogs([]);
    }
    setIsStreaming(!isStreaming);
  };

  const clearStreamLogs = () => {
    setStreamLogs([]);
  };

  const exportLogs = () => {
    const logs = isStreaming ? streamLogs : (data?.data?.data || []);
    const content = JSON.stringify(logs, null, 2);
    const blob = new Blob([content], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `logs-${format(new Date(), 'yyyy-MM-dd-HHmmss')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const displayLogs = isStreaming ? streamLogs : (data?.data?.data || []);
  const filteredLogs = displayLogs.filter((log) => {
    if (filter && !log.message.toLowerCase().includes(filter.toLowerCase())) {
      return false;
    }
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">System Logs</h2>
          <p className="text-gray-500 mt-1">View and monitor system activity</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={toggleStreaming}
            className={clsx(
              'flex items-center gap-2 px-4 py-2 rounded-lg transition-colors',
              isStreaming
                ? 'bg-red-100 text-red-700 hover:bg-red-200'
                : 'bg-green-100 text-green-700 hover:bg-green-200'
            )}
          >
            {isStreaming ? (
              <>
                <Pause className="w-4 h-4" />
                Stop Streaming
              </>
            ) : (
              <>
                <Play className="w-4 h-4" />
                Start Streaming
              </>
            )}
          </button>
          <button
            onClick={exportLogs}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
          {!isStreaming && (
            <button
              onClick={() => refetch()}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              <RefreshCw className={clsx('w-4 h-4', isLoading && 'animate-spin')} />
              Refresh
            </button>
          )}
        </div>
      </div>

      {/* Streaming indicator */}
      {isStreaming && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
            <span className="text-green-700 font-medium">Live streaming enabled</span>
            <span className="text-green-600 text-sm">
              {streamLogs.length} events captured
            </span>
          </div>
          <button
            onClick={clearStreamLogs}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-green-700 hover:bg-green-100 rounded-lg"
          >
            <Trash2 className="w-4 h-4" />
            Clear
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-600">Filters:</span>
          </div>
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search logs..."
            className="flex-1 px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-perfectted-primary"
          />
          <select
            value={levelFilter}
            onChange={(e) => setLevelFilter(e.target.value)}
            className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-perfectted-primary"
          >
            <option value="">All Levels</option>
            <option value="error">Error</option>
            <option value="warn">Warning</option>
            <option value="info">Info</option>
            <option value="debug">Debug</option>
          </select>
        </div>
      </div>

      {/* Logs List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
          <span className="font-medium text-gray-700">
            {filteredLogs.length} log entries
          </span>
          {data?.data?.pagination && !isStreaming && (
            <span className="text-sm text-gray-500">
              Page 1 of {data.data.pagination.totalPages} ({data.data.pagination.totalItems} total)
            </span>
          )}
        </div>

        <div className="max-h-[600px] overflow-y-auto">
          {isLoading && !isStreaming ? (
            <div className="p-8 text-center text-gray-500">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-perfectted-primary" />
              Loading logs...
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No logs found
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredLogs.map((log) => {
                const config = levelConfig[log.level] || levelConfig.info;
                const Icon = config.icon;

                return (
                  <div
                    key={log.id}
                    className={clsx('px-4 py-3 hover:bg-gray-50 transition-colors', config.bgColor + '/30')}
                  >
                    <div className="flex items-start gap-3">
                      <div className={clsx('p-1.5 rounded', config.bgColor)}>
                        <Icon className={clsx('w-4 h-4', config.color)} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium text-gray-900 truncate">
                            {log.message}
                          </span>
                          <span className="text-xs text-gray-400 flex-shrink-0">
                            {format(new Date(log.createdAt), 'HH:mm:ss.SSS')}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 mt-1">
                          <span className={clsx('text-xs font-medium uppercase', config.color)}>
                            {log.level}
                          </span>
                          {log.source && (
                            <span className="text-xs text-gray-500">
                              Source: <code className="bg-gray-100 px-1 rounded">{log.source}</code>
                            </span>
                          )}
                          {log.orderId && (
                            <Link
                              to={`/orders/${log.orderId}/flow`}
                              className="text-xs text-perfectted-primary hover:underline flex items-center gap-1"
                            >
                              Order: {log.orderId.slice(0, 8)}...
                              <ExternalLink className="w-3 h-3" />
                            </Link>
                          )}
                        </div>
                        {log.context && Object.keys(log.context).length > 0 && (
                          <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-x-auto">
                            {JSON.stringify(log.context, null, 2)}
                          </pre>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={logsEndRef} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
